import areNonNegativeIntegers from "validate.io-nonnegative-integer-array";
import { getFeatureTypes } from "@teselagen/sequence-utils";
import {
  filterSequenceString,
  guessIfSequenceIsDnaAndNotProtein
} from "@teselagen/sequence-utils";
import { filter, some, upperFirst } from "lodash-es";
import pragmasAndTypes from "./pragmasAndTypes.js";
import { forEach } from "lodash-es";
import { map } from "lodash-es";
import { unmangleUrls } from "./unmangleUrls";
import { reformatName } from "./NameUtils.js";
export default function validateSequence(sequence, options = {}) {
  const {
    guessIfProtein,
    guessIfProteinOptions,
    reformatSeqName,
    inclusive1BasedStart,
    inclusive1BasedEnd,
    additionalValidChars,
    allowOverflowAnnotations,
    coerceFeatureTypes
  } = options;
  [
    "isDNA",
    "isOligo",
    "isRNA",
    "isDoubleStrandedDNA",
    "isSingleStrandedDNA",
    "isDoubleStrandedRNA",
    "isProtein"
  ].forEach((k) => {
    if (options[k] !== void 0 && sequence[k] === void 0) {
      sequence[k] = options[k];
    }
  });
  const response = {
    validatedAndCleanedSequence: {},
    messages: []
  };
  if (!sequence || typeof sequence !== "object") {
    throw new Error("Invalid sequence");
  }
  if (!sequence.name) {
    sequence.name = "";
  }
  if (!sequence.extraLines) {
    sequence.extraLines = [];
  }
  if (!sequence.comments) {
    sequence.comments = [];
  }
  if (sequence.description) {
    sequence.description = unmangleUrls(sequence.description);
  }
  const oldName = sequence.name;
  if (reformatSeqName) {
    sequence.name = reformatName(sequence.name);
  }
  if (oldName !== sequence.name) {
    response.messages.push(
      "Name (" + oldName + ") reformatted to " + sequence.name
    );
  }
  if (Array.isArray(sequence.sequence)) {
    sequence.sequence = sequence.sequence.join("");
  }
  if (!sequence.sequence) {
    response.messages.push("No sequence detected");
    sequence.sequence = "";
  }
  if (sequence.isProtein === void 0 && guessIfProtein) {
    sequence.isProtein = !guessIfSequenceIsDnaAndNotProtein(
      sequence.sequence,
      guessIfProteinOptions
    );
  }
  if (sequence.isProtein) {
    const [validChars, warnings] = filterSequenceString(sequence.sequence, {
      name: sequence.name,
      isProtein: true,
      additionalValidChars
    });
    if (validChars !== sequence.sequence) {
      sequence.sequence = validChars;
      response.messages.push(...warnings);
    }
    sequence.type = "PROTEIN";
    sequence.isProtein = true;
    if (!sequence.proteinSequence) {
      sequence.proteinSequence = sequence.sequence;
    }
    sequence.proteinSize = sequence.proteinSequence.length;
  } else {
    const temp = sequence.sequence;
    if (temp !== sequence.sequence && !sequence.isDNA && !sequence.isProtein && sequence.isRNA !== false) {
      sequence.type = "RNA";
      sequence.sequence = temp;
    } else {
      sequence.type = "DNA";
    }
    const [validChars, warnings] = filterSequenceString(sequence.sequence, {
      additionalValidChars,
      ...sequence
    });
    if (validChars !== sequence.sequence) {
      sequence.sequence = validChars;
      response.messages.push(...warnings);
    }
  }
  if (!sequence.size) {
    sequence.size = sequence.isProtein ? sequence.proteinSequence.length * 3 : sequence.sequence.length;
  }
  let circularityExplicitlyDefined;
  if (sequence.circular === false || sequence.circular === "false" || sequence.circular === -1) {
    sequence.circular = false;
    circularityExplicitlyDefined = true;
  } else if (!sequence.circular) {
    sequence.circular = false;
    circularityExplicitlyDefined = circularityExplicitlyDefined || false;
  } else {
    sequence.circular = true;
  }
  if (!sequence.features || !Array.isArray(sequence.features)) {
    response.messages.push("No valid features detected");
    sequence.features = [];
  }
  sequence.features = sequence.features.filter(function(feature) {
    if (!feature || typeof feature !== "object") {
      response.messages.push("Invalid feature detected and removed");
      return false;
    }
    feature.start = parseInt(feature.start, 10);
    feature.end = parseInt(feature.end, 10);
    if (!feature.name || typeof feature.name !== "string") {
      response.messages.push(
        'Unable to detect valid name for feature, setting name to "Untitled Feature"'
      );
      feature.name = "Untitled Feature";
    }
    if (!allowOverflowAnnotations && (!areNonNegativeIntegers([feature.start]) || feature.start > sequence.size - (inclusive1BasedStart ? 0 : 1))) {
      response.messages.push(
        "Invalid feature start: " + feature.start + " detected for " + feature.name + " and set to 1"
      );
      feature.start = 0;
    }
    if (!allowOverflowAnnotations && (!areNonNegativeIntegers([feature.end]) || feature.end > sequence.size - (inclusive1BasedEnd ? 0 : 1))) {
      feature.end = Math.max(sequence.size - 1, inclusive1BasedEnd ? 0 : 1);
      response.messages.push(
        "Invalid feature end:  " + feature.end + " detected for " + feature.name + " and set to " + (feature.end + 1)
      );
    }
    if (feature.start - (inclusive1BasedStart ? 0 : 1) > feature.end - (inclusive1BasedEnd ? 0 : 1) && sequence.circular === false) {
      if (circularityExplicitlyDefined) {
        response.messages.push(
          "Invalid circular feature detected in explicitly linear sequence. " + feature.name + ". start set to 1"
        );
        feature.start = 0;
      } else {
        response.messages.push(
          "Circular feature detected in implicitly linear sequence. Setting sequence to be circular."
        );
        sequence.circular = true;
      }
    }
    feature.strand = parseInt(feature.strand, 10);
    if (feature.strand === -1 || feature.strand === false || feature.strand === "false" || feature.strand === "-") {
      feature.strand = -1;
    } else {
      feature.strand = 1;
    }
    let invalidFeatureType;
    if (feature.type && typeof feature.type === "string" && feature.type.toLowerCase() === "primer") {
      feature.type = "primer_bind";
    }
    if (!feature.type || typeof feature.type !== "string" || !getFeatureTypes({ includeHidden: true }).some(function(featureType) {
      if (featureType.toLowerCase() === feature.type.toLowerCase()) {
        feature.type = featureType;
        return true;
      }
      return false;
    })) {
      if (coerceFeatureTypes || !feature.type) {
        response.messages.push(
          'Invalid feature type detected:  "' + feature.type + '" within ' + feature.name + ". set type to misc_feature"
        );
        if (typeof feature.type === "string") {
          invalidFeatureType = feature.type;
        }
        feature.type = "misc_feature";
      }
    }
    if (!feature.notes) {
      feature.notes = {};
    }
    if (invalidFeatureType) {
      if (!feature.notes.featureType) {
        feature.notes.featureType = [];
      }
      feature.notes.featureType.push(invalidFeatureType);
    }
    if (feature.notes.label) {
      delete feature.notes.label;
    } else if (feature.notes.gene) {
      delete feature.notes.gene;
    } else if (feature.notes.name) {
      delete feature.notes.name;
    }
    if (feature.notes.color) {
      feature.color = feature.notes.color[0] || feature.color;
      delete feature.notes.color;
    }
    if (feature.notes.labelColor) {
      feature.labelColor = feature.notes.labelColor[0] || feature.labelColor;
      delete feature.notes.labelColor;
    }
    if (feature.notes.pragma && some(feature.notes.pragma, (p) => p === "overlapsSelf")) {
      feature.overlapsSelf = true;
      feature.notes.pragma = filter(
        feature.notes.pragma,
        (p) => p !== "overlapsSelf"
      );
    }
    feature.notes.note && some(feature.notes.note, (n) => {
      if (n && typeof n === "string" && n.toLowerCase().includes("sequence:")) {
        feature.notes.note = filter(
          feature.notes.note,
          (p) => p && !p.toLowerCase().includes("sequence:")
        );
        if (feature.notes.note.length === 0) {
          delete feature.notes.note;
        }
        const match = n.match(/sequence:[ \r\n.]*[a-zA-Z]*/i);
        if (match && match[0])
          feature.bases = match[0].replace(/\s/g, "").replace("sequence:", "");
        return true;
      }
    });
    feature.notes.primerBindsOn && some(feature.notes.primerBindsOn, (n) => {
      if (n) {
        feature.primerBindsOn = n;
        delete feature.notes.primerBindsOn;
      }
    });
    for (const { pragma, type } of pragmasAndTypes) {
      if (options[`accept${upperFirst(type)}`] !== false && //acceptParts, acceptWarnings,
      feature.notes.pragma && some(feature.notes.pragma, (p) => p === pragma)) {
        if (!sequence[type]) {
          sequence[type] = [];
        }
        if (type !== "parts") {
          feature.type = type.slice(0, -1);
        }
        delete feature.notes.pragma;
        sequence[type].push(feature);
        return false;
      }
    }
    forEach(feature.notes, (noteArray, key) => {
      feature.notes[key] = map(noteArray, (note) => {
        return unmangleUrls(note);
      });
    });
    return true;
  });
  response.validatedAndCleanedSequence = sequence;
  return response;
}
